import { cloneDeep } from 'lodash';
import React, { useState, useEffect } from 'react';

import { Loader } from 'components/misc/loader-utils';
import { Columns } from 'components/base/megatable/column-service';
import StatTable from 'components/base/megatable/stat-table';
import PacingIntervalDropdown from 'components/base/dropdowns/pacinginterval-dropdown';
import ColumnsDropdown from 'components/base/dropdowns/columns-dropdown';
import { PublisherDropdown, StatusDropdown, TypeDropdown } from './multiselect-dropdowns';

import { getPacingIntervalOptions } from 'components/admin/bid-center/utils';
import { session } from 'services/session';
import { api } from 'services/api';

import './overview-table.scss';

const defaultPacingOptions = getPacingIntervalOptions();
const defaultStatusOptions = [
	{label: 'Live', name: 'live', selected: true},
	{label: 'Upcoming', name: 'upcoming', selected: true},
	{label: 'Completed', name: 'completed', selected: true},
	{label: 'Paused', name: 'paused', selected: true},
];
const defaultTypeOptions = [
	{label: 'Marketplace', name: 'marketplace', selected: true},
	{label: 'Direct Deal', name: 'direct_deal', selected: true},
];

const gaCategory = 'Campaign Performance Overview';

function OverviewTable({ merchId, statType, history, match, location, dateRange={} }) {
	/* State */
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState([]);
	const [totals, setTotals] = useState({});
	const [statusOptions, setStatusOptions] = useState(defaultStatusOptions);
	const [typeOptions, setTypeOptions] = useState(defaultTypeOptions);
	const [pacingOptions, setPacingOptions] = useState(defaultPacingOptions);
	const [publisherOptions, setPublisherOptions] = useState([]);
	const [publisherLabel, setPublisherLabel] = useState('Publisher');
	const [numPubsSelected, setNumPubsSelected] = useState(0);

	const tableColumns = new Columns().getColumnsForTable(statType);
	const defaultColumns = cloneDeep(tableColumns);
	const [columns, setColumns] = useState(tableColumns);

	/* Constants */
	const orgId = session.orgId;
	const endpoint = `/api/v0/merchants/${orgId}/strategy/overview/stats/`;
	const pubListEndpoint = `/api/v0/merchants/${orgId}/strategy/type_ahead/`;

	/* Effects */
	useEffect(() => {
		fetchPublisherList();
	}, []);

	useEffect(() => {
		fetchData();
	}, [statusOptions, typeOptions, pacingOptions, dateRange, publisherOptions]);

	/* Methods */
	async function fetchData() {
		setLoading(true);
		const params = getParams();
		const resp = await api.get(endpoint, params);
		const stats = resp.data.data[0].stats;
		const totals = resp.data.data[0].totals;
		setStats(stats);
		setTotals(totals);
		setLoading(false);
	}

	async function fetchPublisherList() {
		const resp = await api.get(pubListEndpoint, {type: 'publisher'});
		const pubList = resp.data.data[0].stats;

		const formattedPubs = pubList.map((pub) => {
			return {
				label: pub.full_name,
				name: pub.pub_id,
				selected: false,
			};
		}).sort((pub1, pub2) => {
			return pub1.label.toLowerCase() > pub2.label.toLowerCase() ? 1 : -1;
		});
		setPublisherOptions(formattedPubs);
	}

	function formatOptions(options) {
		return options
			.filter((option) => option.selected)
			.map((option) => option.name);
	}

	function getParams() {
		const selectedPacing = pacingOptions.reduce((acc, curr) =>
			curr.selected ? acc.concat([`${curr.value[0]}-${curr.value[1]}`]) : acc, []);
		const selectedTypes = formatOptions(typeOptions);
		const selectedStatuses = formatOptions(statusOptions);
		const selectedPubs = formatOptions(publisherOptions);
		return {
			date_from: dateRange.startDate.format('YYYY-MM-DD'),
			date_to: dateRange.endDate.format('YYYY-MM-DD'),
			type: selectedTypes.join(','),
			status: selectedStatuses.join(','),
			pacing_intervals: selectedPacing.join(','),
			publishers_selected: [0, publisherOptions.length].includes(selectedPubs.length)
				? null : selectedPubs.join(','),
			use_production_data: true,
			admin_stats: !!session.isAdminView,
		};
	}

	function updatePubs(pubs) {
		const selectedPubs = pubs.filter((pub) => pub.selected);
		// Sorts pubs by selected and then by name
		const sortedPubs = pubs.sort((a, b) => {
			return a.selected !== b.selected
				? a.selected
					? -1 : 1
				: a.label > b.label
					? 1 : -1;
		});
		const pubLabel = selectedPubs.length === 0
			? 'Publisher'
			: selectedPubs.length > 1
				? `${selectedPubs.length} Publishers`
				: selectedPubs[0].label;

		setPublisherOptions(sortedPubs);
		setPublisherLabel(pubLabel);
		setNumPubsSelected(selectedPubs.length);
	}

	/* Render */
	const table = !loading ? (
		<StatTable
			stats={stats}
			totals={totals}
			showTotals={!loading}
			columns={columns}
			sortDisabledFields={[]}
			updateSortParams={() => {}}
			statType={statType}
			tableName={null}
			sortOptions={{}}
			disableEditUrl={true}
			history={history}
			match={match}
			location={location}
			classOverride="base-search-table overview-table">
		</StatTable>
	) : null;

	const dropdowns = !loading ? (
		<div className="overview-dropdowns">
			<PublisherDropdown
				gaEvent={{
					category: gaCategory,
					label: 'Publisher Dropdown',
				}}
				items={publisherOptions}
				updateOptions={updatePubs}
				staticFields={[]}
				allSelected={numPubsSelected === publisherOptions.length}
				numSelected={numPubsSelected}
				label={publisherLabel}
			/>
			<StatusDropdown
				gaEvent={{
					category: gaCategory,
					label: 'Campaign Statuses Dropdown',
				}}
				items={statusOptions}
				updateOptions={setStatusOptions}
			/>
			<TypeDropdown
				gaEvent={{
					category: gaCategory,
					label: 'Campaign Type Dropdown',
				}}
				items={typeOptions}
				updateOptions={setTypeOptions}
			/>
			{/* TODO: Uncomment this once pacing calculations are fixed */}
			{/* <PacingIntervalDropdown */}
			{/*	gaEvent={{ */}
			{/*		category: gaCategory, */}
			{/*		label: 'Pacing Interval Dropdown', */}
			{/*	}} */}
			{/*	items={pacingOptions} */}
			{/*	updatePacingIntervalOptions={setPacingOptions} */}
			{/* /> */}
			<ColumnsDropdown
				gaEvent={{
					category: gaCategory,
					label: `Column Dropdown`,
				}}
				items={columns}
				updateColumns={setColumns}
				resetColumns={() => setColumns(defaultColumns)}
				statType={statType}
				staticFields={[]}
			/>
		</div>
	) : null;

	return (
		<div className="overview-table">
			{dropdowns}
			{table}
			<Loader loading={loading} />
		</div>
	);
}

export default OverviewTable;
